import {
  IAutomationActionTemplateDoceboEnroll,
  IAutomationTemplate
} from '@dadohr/shared/domainTypes/experience';
import { compact } from 'lodash';
import React, { SetStateAction } from 'react';
import { AutocompleteSingle } from '../../../../../../../components/Autocomplete';
import { DataFieldWithLabel } from '../../../../../../../components/DataFieldWithLabel';
import { FormRow } from '../../../../../../../components/Form/FormRow';
import { TextFieldLoader } from '../../../../../../../components/TextFieldLoader';
import { useUnsafeTranslation } from '../../../../../../../i18next';
import { FlexContainerVertical } from '../../../../../../../layout/Flex';
import { joinWithCommaAndAnd } from '../../../../../../../services/enumerate';
import { useMappedLoadingValue } from '../../../../../../../services/firebase';
import { useDoceboCourses } from '../../../../../../Org/pages/Integrations/integrations/docebo/service';
import { AnchorField } from '../../../../Anchors';
import { TaskBuilderContext } from '../../../TaskBuilder/types';
import { ActionActorSelectorMulti, actionActorToString } from '../../shared';
import {
  AutomationBuilderActionValidationState,
  AutomationBuilderActionValidationStateItem,
  useFieldValidator,
  VALIDATORS_DOCEBO_ENROLL
} from '../../validation';

const CourseSelector = ({
  orgId,
  value,
  onChange,

  helperText,
  error
}: {
  orgId: string;
  value: IAutomationActionTemplateDoceboEnroll['target'];
  onChange: (
    nextValue: IAutomationActionTemplateDoceboEnroll['target']
  ) => void;

  helperText?: React.ReactNode;
  error?: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  const [courses] = useMappedLoadingValue(useDoceboCourses(orgId), (xs) => {
    return compact(
      xs.map((x) => ({
        id: x.id,
        name: x.title,
        type: 'COURSE' as const
      }))
    );
  });
  if (!courses) {
    return <TextFieldLoader fullWidth />;
  }
  return (
    <AutocompleteSingle
      label={t('Assign to course')}
      fullWidth
      value={value}
      onChange={(nextValue) => nextValue && onChange(nextValue)}
      options={courses}
      getOptionLabel={(o) => o.name}
      renderOption={(o) => <div>{o.name}</div>}
    />
  );
};

export const ActionSettingsDoceboEnroll = ({
  value,
  automation,
  onChange,
  expTpl,
  validation,
  setValidation,
  anchorFields
}: {
  value: IAutomationActionTemplateDoceboEnroll;
  automation: IAutomationTemplate;
  onChange: (nextValue: IAutomationActionTemplateDoceboEnroll) => void;
  expTpl: TaskBuilderContext;
  validation: AutomationBuilderActionValidationStateItem | null;
  setValidation: React.Dispatch<
    SetStateAction<AutomationBuilderActionValidationState>
  >;
  anchorFields: AnchorField[];
}) => {
  const { t } = useUnsafeTranslation();

  const validatePplToAdd = useFieldValidator(
    setValidation,
    VALIDATORS_DOCEBO_ENROLL,
    'pplToAdd'
  );
  const invalidPpltoAdd = validation?.fields.pplToAdd || null;
  return (
    <div>
      <FlexContainerVertical spacing={2} fullWidth>
        <ActionActorSelectorMulti
          label={t('Who should be assigned')}
          value={value.pplToAdd}
          onChange={(nextPplToAdd) => {
            const nextValue = { ...value, pplToAdd: nextPplToAdd };
            invalidPpltoAdd && validatePplToAdd(nextValue, expTpl, automation);
            return onChange(nextValue);
          }}
          expTpl={expTpl}
          fullWidth
          error={!!invalidPpltoAdd}
          helperText={invalidPpltoAdd}
          onBlur={() => validatePplToAdd(value, expTpl, automation)}
        />
        <CourseSelector
          orgId={expTpl.orgId}
          value={value.target}
          helperText={''}
          error={false}
          onChange={(nextTarget) => {
            onChange({ ...value, target: nextTarget });
          }}
        />
      </FlexContainerVertical>
    </div>
  );
};

export const ActionSettingsDoceboEnrollReadOnly = ({
  value,
  expTpl
}: {
  value: IAutomationActionTemplateDoceboEnroll;
  expTpl: TaskBuilderContext;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <div>
      <FormRow>
        <DataFieldWithLabel label={t('Who should be assigned')}>
          {joinWithCommaAndAnd(
            value.pplToAdd.map((p) =>
              actionActorToString(p, expTpl.supportingActors, t)
            ),
            t
          )}
        </DataFieldWithLabel>
      </FormRow>

      <FormRow>
        <DataFieldWithLabel label={t('Assign to')}>
          {value.target.name}
        </DataFieldWithLabel>
      </FormRow>
    </div>
  );
};
